<template>
    <div>
        <v-tabs grow :value="selectedProductTypeIndex" @change="changeTab" height="32">
            <v-tab v-for="(item, index) in productTypes" :key="item">
                {{ t(item.replace("_", " ")) }}
                <span class="ml-1" v-if="exportMode || priceMode"> ({{ selectedItemCount(item) }})</span>
            </v-tab>
        </v-tabs>
        <v-card flat style="border-top: 1px solid lightgray">
            <v-overlay :value="loadingProductList">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>
            <v-flex>
                <v-data-table
                    v-model="selectedExportProducts"
                    :footer-props="footerProps"
                    :headers="productsTableHeaders"
                    :items="exportMode || priceMode ? productsWithSelectedItems : products"
                    :items-per-page="500"
                    :options.sync="options"
                    :server-items-length="totalProductCount"
                    :show-select="exportMode || priceMode"
                    class="product-table"
                    dense
                >
                    <template
                        v-for="h in productsTableHeaders"
                        v-slot:[`header.${h.value}`]="{ header }"
                        :class="'text-' + header.align"
                    >
                        {{ header.text ? t(header.text) : "-" }}
                    </template>
                    <template v-slot:header.status="{ header }">
                        <v-menu v-model="showStatusMenu" :close-on-content-click="false" offset-y>
                            <template v-slot:activator="{ on }">
                                <span v-on="on" style="cursor: pointer; white-space: nowrap">{{ "Status" | t }}</span>
                            </template>
                            <v-card class="px-5 pb-5" style="z-index: 10">
                                <v-row>
                                    <v-card flat>
                                        <v-row>
                                            <v-list dense>
                                                <v-list-item v-for="status in statuses" :key="status">
                                                    <v-list-item-action>
                                                        <v-switch
                                                            v-model="selected.includes(status)"
                                                            color="primary"
                                                            @change="editSelected($event, status)"
                                                        />
                                                    </v-list-item-action>
                                                    <v-list-item-title
                                                        >{{ t(status.charAt(0).toUpperCase() + status.slice(1)) }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-row>
                                        <v-card-actions>
                                            <v-spacer />
                                            <v-btn color="primary" x-small @click="showStatusMenu = false">
                                                {{ "Filter" | t }}
                                            </v-btn>
                                        </v-card-actions>
                                    </v-card>
                                </v-row>
                            </v-card>
                        </v-menu>
                    </template>
                    <template v-if="!exportMode && !priceMode" v-slot:item="{ item }">
                        <tr
                            :key="'product_' + item.id"
                            :class="{
                                activeTableRow: activeProduct && parseInt(item.id) === parseInt(activeProduct.id),
                            }"
                            @click="handleTableRowClick(item)"
                        >
                            <td
                                v-for="(row, index) in productsTableHeaders"
                                :class="{
                                    lastColumn:
                                        index === productsTableHeaders.length - 1 && productsTableHeaders.length > 2,
                                    penultimateColumn:
                                        index === productsTableHeaders.length - 2 && productsTableHeaders.length > 2,
                                }"
                            >
                                <span
                                    v-if="row.value === 'status'"
                                    :style="{ 'background-color': getStatusColor(item.status) }"
                                    class="dot"
                                ></span>
                                <span
                                    v-else-if="
                                        row.value === 'list_net_price' ||
                                        row.value === 'export_price' ||
                                        row.value === 'cost_price' ||
                                        row.value === 'own_price'
                                    "
                                    >{{ `${Math.round(item[row.value] * 100) / 100}€` }}</span
                                >
                                <span v-else-if="row.value === 'neto_weight'">
                                    {{
                                        !item[row.value]
                                            ? "-"
                                            : `${(Math.round(item[row.value] * 100) / 100).toFixed(2)}`
                                    }}
                                </span>
                                <span v-else>{{ getRowValue(item, row) }}</span>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-flex>
        </v-card>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import Store from "../../store";
import PermissionMixin from "@/mixins/permissionMixin";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "products-list",
    data() {
        return {
            footerProps: { "items-per-page-options": [25, 50, 100, 500] },
            selectedExportProducts: [],
            options: this.tableOptions,
            scrollMem: { left: 0, top: 0 },
            showStatusMenu: false,
            statuses: ["active", "in development", "inactive", "special order", "private label"],
            selected: [],
            needRefresh: false,
        };
    },
    mounted() {
        //Store.dispatch("ProductStore/getProductList", { options: this.tableOptions, search: this.searchProductStrings });
        let exportProducts = JSON.parse(localStorage.getItem("exportProducts"));
        if (exportProducts) {
            this.selectedExportProducts = exportProducts;
            this.setExportProducts(exportProducts);
        }
        if (this.drawerOpen) {
            document.documentElement.style.overflow = "hidden";
        }
        this.selected = this.statusForFiltering;
    },
    watch: {
        selectedProductIndex() {
            console.log(this.selectedProductIndex);
        },
        selectedExportProducts() {
            localStorage.setItem("exportProducts", JSON.stringify(this.selectedExportProducts));
            this.setExportProducts(this.selectedExportProducts);
        },
        options(val) {
            Store.commit("ProductStore/setTableOptions", val);
        },
        drawerOpen() {
            if (this.drawerOpen) {
                document.documentElement.style.overflow = "hidden";
            } else {
                document.documentElement.style.overflow = "auto";
            }
        },
        async showStatusMenu(bool) {
            if (!bool) {
                if (this.selected.length === 0) this.selected = [...this.statuses];
                if (this.selected !== this.statusForFiltering) this.needRefresh = true;
                await this.setStatusForFiltering(this.selected);
                await this.setProductsTableHeaders();
                await this.saveUserProductSearchColumnSettings(this.productsTableHeaders);
                if (this.needRefresh) {
                    await this.getProductList({ options: this.tableOptions, searchStrings: this.searchProductStrings });
                    this.needRefresh = false;
                }
            }
        },
    },
    computed: {
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
        ...mapState("ProductStore", [
            "products",
            "loadingProductList",
            "activeProduct",
            "searchProductStrings",
            "exportMode",
            "priceMode",
            "exportProducts",
            "tableOptions",
            "totalProductCount",
            "drawerOpen",
            "listSettingsColumns",
            "productsTableHeaders",
            "statusForFiltering",
            "productTypes",
            "selectedProductTypeIndex",
        ]),
        productsWithSelectedItems() {
            const filteredSelected = this.filteredSelectedItems(this.productTypes[this.selectedProductTypeIndex]);
            const allProducts = [...filteredSelected, ...this.products];
            const uniqueProducts = new Map();
            allProducts.forEach((p) => uniqueProducts.set(p.id, p));
            return Array.from(uniqueProducts.values());
        },
        filteredSelectedItems() {
            return (type = null) => this.selectedExportProducts.filter((p) => !type || p.product_type === type);
        },
    },
    methods: {
        ...mapMutations("ProductStore", [
            "setExportProducts",
            "setProductsTableHeaders",
            "setStatusForFiltering",
            "setSelectedProductTypeIndex",
        ]),
        ...mapActions("UserStore", ["saveUserProductSearchColumnSettings"]),
        ...mapActions("ProductStore", ["getProductList"]),
        ...mapActions("SearchHistoryStore", ["getSearchHistory"]),

        handleTableRowClick(product) {
            if (!this.hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.VIEW)) {
                return;
            }

            if (this.$route.name !== "product-view") {
                this.$router.push({ name: "product-view", params: { productId: product.id } });

                return;
            }
            if (
                this.$route.name === "product-view" &&
                parseInt(product.id) !== parseInt(this.$route.params.productId)
            ) {
                this.$router.push({ name: "product-view", params: { productId: product.id } });
                Store.dispatch("ProductStore/getProductData", product.id);
            }
        },
        getStatusColor(status) {
            if (status === "active") {
                return "#4CAF50";
            } else if (status === "in development") {
                return "#FB8C00";
            } else if (status === "special order") {
                return "#005DFF";
            } else if (status === "private label") {
                return "#FF0051";
            } else {
                return "#999999";
            }
        },
        getRowValue(item, row) {
            if (item.data && item.data[row.value] != null) {
                return item.data[row.value];
            } else if (item[row.value] != null) {
                return item[row.value];
            } else {
                return "-";
            }
        },
        editSelected(bool, status) {
            if (bool) {
                this.selected.push(status);
            } else {
                let index = this.selected.indexOf(status);
                this.selected.splice(index, 1);
            }
        },
        async changeTab(index) {
            await this.setSelectedProductTypeIndex(index);
        },
        selectedItemCount(type) {
            return this.filteredSelectedItems(type).length;
        },
    },
};
</script>

<style scoped>
.activeTableRow {
    background-color: #f41823;
    color: #fff;
}

.activeTableRow:hover {
    background-color: #e11520 !important;
    color: #fff !important;
}

.lastColumn {
    text-align: center;
}

.penultimateColumn {
    text-align: end;
}

.dot {
    margin-top: 4px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    display: inline-block;
}

.v-data-table-header {
    background-color: #f0f0f0;
}

.preventscroll {
    overflow-y: scroll;
    width: 100%;
}

td {
    height: 25px !important;
}

::v-deep .v-data-table-header th {
    white-space: nowrap;
}

.product-table {
    overflow: auto;
}

::v-deep .v-tab {
    opacity: 0.6;
}

::v-deep .v-tab--active,
::v-deep .v-tab:hover {
    transition: 0.3s;
    opacity: 1;
}
</style>
